<template>
	<div>
		<core-loading v-if="isLoading || shouldFetchRuleData" class="rulesets-loading" size="40px" />

		<div v-if="showRules" ref="rulesWrap" class="omapi-rules-list__summary" :style="rulesetWrapper">
			<div v-for="(ruleset, index) in rulesets" :key="index" class="omapi-rules-list__ruleset">
				<h2 v-if="rulesets.length > 1" class="omapi-rules-list__ruleset-title">
					<strong>{{ ruleset.name }}</strong>
				</h2>
				<campaigns-rule-summary-ruleset :ruleset="ruleset" :index="index" @changedOutput="resetHeight" />
			</div>

			<div v-if="showToggle" :style="rulesetFadeOut" class="omapi-rules-list__fadeout"></div>
		</div>

		<div v-if="showToggle" class="omapi-rules-list__toggle" @click="min = !min">
			<core-button type="secondary">{{ min ? 'More' : 'Less' }}</core-button>
		</div>

		<transition name="component-fade-slow">
			<core-alert v-if="isError" type="warn" style="flex-direction: column">
				<!-- eslint-disable-next-line vue/no-v-html -->
				<div class="alert-message" v-html="isError" />
			</core-alert>
		</transition>
	</div>
</template>

<script lang="js">
import { appData, bulkWatchHandler } from '@/utils';
import { campaign } from '@/campaigns/mixins/campaign';

import { getErrorMessage } from '@/store/alerts/mutations.js';

/**
 * Campaign Rules Summary
 *
 * @since 2.0.0
 */
export default {
	mixins: [campaign],
	props: {
		/**
		 * Campaign
		 */
		campaign: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			min: false,
			height: 0,
		};
	},
	computed: {
		shouldFetchRuleData() {
			return !this.$store.getters.isFetching('rulesetData') && !this.$store.getters.isFetched('rulesetData');
		},
		isLoading() {
			return this.$store.getters.isLoading('rulesets');
		},
		showToggle() {
			return this.hasRules && 95 < this.height;
		},
		rulesets() {
			return this.campaign.rulesets;
		},
		isError() {
			return this.rulesets instanceof Error ? getErrorMessage(this.rulesets) : false;
		},
		hasRules() {
			return !this.isError && !this.isLoading && this.rulesets && 0 < this.rulesets.length;
		},
		showRules() {
			return this.hasRules && !this.shouldFetchRuleData;
		},
		rulesetWrapper() {
			return {
				height: this.min ? '95px' : 'auto',
				overflowY: this.min ? 'hidden' : 'auto',
			};
		},
		rulesetFadeOut() {
			return {
				display: this.min ? 'block' : 'none',
			};
		},
		rulesetData() {
			return appData.get('rulesetData');
		},
		campaignRules() {
			return this.getRules((rule) => {
				return this.rulesetData.isCampaignRule(rule) && !rule.operator.includes('folder');
			});
		},
	},
	watch: {
		...bulkWatchHandler(['id', 'hasRules', 'showRules'], 'resetHeight'),
		campaignRules: function (newCampaignRules, oldCampaignRules) {
			// If we just finished loading the campaign's rulesets, then we need
			// to fetch any rule campaigns.
			if (0 === oldCampaignRules.length && 0 < newCampaignRules.length) {
				this.fetchCampaignRuleCampaigns();
			}
		},
	},
	mounted() {
		this.fetchCampaignRuleCampaigns();
		this.min = false;
		this.setHeight();
	},
	methods: {
		fetchCampaignRuleCampaigns() {
			this.$store.dispatch('campaigns/fetchRulesetData');

			const campaignIds = this.campaignRules.flatMap((r) => r.value);

			this.$store.dispatch('campaigns/fetchCampaigns', {
				campaignIds,
				loading: false,
				ignoreSiteId: true,
			});
		},
		resetHeight() {
			this.min = false;
			this.$nextTick(() => {
				this.setHeight();
			});
		},
		setHeight() {
			this.height = this.$get('$refs.rulesWrap.offsetHeight', 0);
			this.min = true;
		},
	},
};
</script>

<style lang="scss" scoped>
.omapi-rules-list {
	&__ruleset {
		max-width: 100%;
		overflow-wrap: break-word;
		margin-top: 15px;

		&-title {
			font-size: 14px;
			margin: 0 0 8px;
		}

		&:first-child {
			margin-top: 0;
		}
	}
	&__summary {
		position: relative;
	}
	&__fadeout {
		background: linear-gradient(transparent, #fff);
		bottom: 0;
		display: block;
		height: 50px;
		left: 0;
		position: absolute;
		width: 100%;
	}
}
.rulesets-loading {
	margin: 0 0 20px;
}
</style>
